import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react'

/** Where a field lives, in the words an API reference uses. */
const LOCATIONS = ['', 'query', 'path', 'body', 'header'] as const

/**
 * One field of an API reference.
 *
 * The facts sit on one line above the prose, in the SAME order the published
 * block prints them — name, place, type, whether it is needed, what it
 * defaults to — so a writer is not reading one arrangement here and another in
 * Preview. They are inputs rather than a form below the block, because an API
 * page is dozens of these and a form each would bury the descriptions.
 */
export function FieldView({ node, updateAttributes, editor }: NodeViewProps) {
  const attr = (key: string): string => (node.attrs[key] as string | null) ?? ''
  const set = (key: string, value: string): void =>
    updateAttributes({ [key]: value.trim() === '' ? null : value })
  const disabled = !editor.isEditable
  return (
    <NodeViewWrapper className="be-field" data-sls="field">
      <div className="be-field__head" contentEditable={false}>
        <input
          className="be-inline-input be-field__name"
          value={attr('name')}
          placeholder="Name"
          disabled={disabled}
          onChange={(e) => set('name', e.target.value)}
        />
        <select
          className="be-field__in"
          value={attr('in')}
          disabled={disabled}
          aria-label="Where the field goes"
          onChange={(e) => set('in', e.target.value)}
        >
          {LOCATIONS.map((where) => (
            <option key={where || 'none'} value={where}>
              {where === '' ? 'anywhere' : where}
            </option>
          ))}
        </select>
        <input
          className="be-inline-input be-field__type"
          value={attr('type')}
          placeholder="Type"
          disabled={disabled}
          onChange={(e) => set('type', e.target.value)}
        />
        <label className="be-field__required">
          <input
            type="checkbox"
            checked={node.attrs['required'] === true}
            disabled={disabled}
            onChange={(e) => updateAttributes({ required: e.target.checked })}
          />
          required
        </label>
        <input
          className="be-inline-input be-field__default"
          value={attr('default')}
          placeholder="Default"
          disabled={disabled}
          onChange={(e) => set('default', e.target.value)}
        />
      </div>
      <NodeViewContent className="be-field__body" />
    </NodeViewWrapper>
  )
}
